import { ChevronDown, Menu } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { components } from '@/types/strapi';
import ProductMenu from './menu-sections/product-menu';

type Megamenu = components['schemas']['GlobalMegamenuComponent'];

/**
 * Menu for visitors with JavaScript disabled. The Radix mega menu and the
 * mobile sheet only open through JS, so this is a CSS-only copy that looks the
 * same: hover/focus opens the panel on desktop, a native <details> drawer is
 * used on small screens. Everything inside <noscript> is ignored when
 * JavaScript is on.
 */
export function NoScriptNavigation({
	megamenu,
	variant,
}: {
	megamenu: Megamenu[];
	/** desktop: centre of the header row; mobile: next to the action buttons */
	variant: 'desktop' | 'mobile';
}) {
	if (!megamenu?.length) return null;

	return (
		<noscript>
			{/* Hide the JS-only controls that cannot work without a script */}
			<style>{'.js-only{display:none !important}'}</style>

			{variant === 'desktop' && (
				<div className="mx-2 hidden flex-1 justify-center xl:flex">
					<ul className="relative flex items-center gap-1">
						{megamenu.map((item, index) => (
							<li key={index} className="group/nav">
								<span
									tabIndex={0}
									className="inline-flex h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-white px-2.5 py-2 text-sm font-medium transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none group-hover/nav:bg-gray-50 group-hover/nav:text-blue-500 min-[1440px]:px-4">
									{item.icon?.url && (
										<Image
											src={item.icon.url}
											alt=""
											width={20}
											height={20}
											className="hidden h-4 w-4 min-[1440px]:block"
										/>
									)}
									{item.name}
									<ChevronDown
										className="relative top-[1px] ml-1 h-3 w-3 transition duration-200 group-hover/nav:rotate-180"
										aria-hidden="true"
									/>
								</span>
								{item.submenu && item.icon && (
									<div className="invisible absolute left-0 top-full z-50 pt-1.5 opacity-0 transition-opacity duration-150 group-focus-within/nav:visible group-focus-within/nav:opacity-100 group-hover/nav:visible group-hover/nav:opacity-100">
										<div className="w-[min(900px,90vw)] p-1">
											<ProductMenu
												item={
													item.submenu as components['schemas']['ItemsSubmenuContentComponent'][]
												}
												name={item.name || 'Name'}
												icon={item.icon}
											/>
										</div>
									</div>
								)}
							</li>
						))}
					</ul>
				</div>
			)}

			{variant === 'mobile' && (
				<details className="xl:hidden">
					<summary
						aria-label="Open menu"
						className="flex h-10 w-10 cursor-pointer list-none items-center justify-center rounded-md hover:bg-gray-100 [&::-webkit-details-marker]:hidden">
						<Menu className="h-8 w-8" />
					</summary>
					<div className="absolute left-0 right-0 top-full max-h-[80vh] overflow-y-auto border-t bg-white px-4 py-4 shadow-xl">
						{megamenu.map((item, index) => (
							<details key={index} className="border-b">
								<summary className="flex cursor-pointer list-none items-center gap-2 px-2 py-4 text-base font-medium [&::-webkit-details-marker]:hidden">
									{item.icon?.url && (
										<Image
											src={item.icon.url}
											alt=""
											width={20}
											height={20}
											className="h-5 w-5"
										/>
									)}
									{item.name}
								</summary>
								<div className="space-y-3 pb-3 pl-2">
									{item.submenu?.map((category, catIndex) => (
										<div key={catIndex}>
											<p className="px-2 text-sm font-semibold text-gray-900">
												{category.category}
											</p>
											<ul>
												{category.submenu_items?.map(
													(link, linkIndex) =>
														link.href ? (
															<li key={linkIndex}>
																<Link
																	href={
																		link.href
																	}
																	className="block px-2 py-2 text-sm text-gray-700 hover:text-blue-600">
																	{link.name}
																</Link>
															</li>
														) : null,
												)}
											</ul>
										</div>
									))}
								</div>
							</details>
						))}
					</div>
				</details>
			)}
		</noscript>
	);
}
